import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import CommunityCard from './CommunityCard';
import { getCommunities } from '../../api/communities';

/**
 * CommunityList component for displaying a list of communities
 * @param {Object} props - Component props
 * @param {Object} props.filters - Filter options
 * @param {string} props.layout - Layout style (grid or list)
 * @param {boolean} props.showPagination - Whether to show pagination
 * @param {Function} props.onMembershipChange - Callback for membership change
 * @returns {JSX.Element} CommunityList component
 */
const CommunityList = ({
  filters = {},
  layout = 'grid',
  showPagination = true,
  onMembershipChange,
}) => {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    pages: 1,
    total: 0,
  });
  
  // Fetch communities on mount and when filters or page changes
  useEffect(() => {
    fetchCommunities(pagination.page);
  }, [filters, pagination.page]);
  
  // Fetch communities
  const fetchCommunities = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const queryParams = {
        page,
        limit: pagination.limit,
        ...filters,
      };
      
      const response = await getCommunities(queryParams);
      
      setCommunities(response.data.data.communities);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching communities:', error);
      setError('Failed to load communities. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      page,
    }));
  };
  
  // Handle membership change
  const handleMembershipChange = (communityId, isMember) => {
    if (onMembershipChange) {
      onMembershipChange(communityId, isMember);
    }
  };
  
  // Show loading state
  if (loading && communities.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="lg" />
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }
  
  // Show empty state
  if (communities.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No communities found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {filters.search
            ? `No communities match "${filters.search}"`
            : 'Try creating a new community or adjusting your filters.'}
        </p>
      </div>
    );
  }
  
  // Render grid layout
  if (layout === 'grid') {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communities.map((community) => (
            <CommunityCard
              key={community._id}
              community={community}
              onMembershipChange={handleMembershipChange}
            />
          ))}
        </div>
        
        {/* Pagination */}
        {showPagination && pagination.pages > 1 && (
          <div className="mt-8">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    );
  }
  
  // Render list layout
  return (
    <div>
      <div className="space-y-4">
        {communities.map((community) => (
          <div
            key={community._id}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <CommunityCard
              community={community}
              compact
              onMembershipChange={handleMembershipChange}
            />
          </div>
        ))}
      </div>
      
      {/* Pagination */}
      {showPagination && pagination.pages > 1 && (
        <div className="mt-8">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

CommunityList.propTypes = {
  filters: PropTypes.object,
  layout: PropTypes.oneOf(['grid', 'list']),
  showPagination: PropTypes.bool,
  onMembershipChange: PropTypes.func,
};

export default CommunityList;